// The page's own facts for the tutorial prompts' conditions (phase E1): `play.airborne` (the hero in the air or
// gliding), `play.foe` (an enemy alert to the hero), `play.target` (the interaction chooser has something),
// `play.found` (places found on the map). The prompts' conditions read these and the story's language together
// (`tutorialConditions`). No browser imports.
import { Conditions, type FactSource } from "@rpg/story";

/** What the play facts read, each frame. */
export interface PlayState {
  airborne: boolean;
  foe: boolean;
  target: boolean;
  found: number;
}

/** The play facts' keys. */
export const PLAY_FACTS = ["airborne", "foe", "target", "found"] as const;

/** The `play` fact source over a reading of the page. */
export function playFacts(read: () => PlayState): FactSource {
  return {
    name: "play",
    get: (k) => read()[k as keyof PlayState] ?? null,
    knows: (k) => (PLAY_FACTS as readonly string[]).includes(k),
    keys: () => PLAY_FACTS,
  };
}

/** The tutorials' conditions: the `play` facts and every source of the shared language (the story's, the rpg layer's,
 *  the town's, the meta layer's), looked up live so a source registered later still answers. */
export function tutorialConditions(shared: Conditions | null, read: () => PlayState): Conditions {
  const c = new Conditions().register(playFacts(read));
  if (!shared) return c;
  // delegate every other name to the shared language
  const own = c.source.bind(c);
  const knows = c.knows.bind(c);
  const fact = c.fact.bind(c);
  c.source = (name: string) => (name === "play" ? own(name) : shared.source(name));
  c.knows = (f: string) => (f.startsWith("play.") ? knows(f) : shared.knows(f));
  c.fact = (f: string) => (f.startsWith("play.") ? fact(f) : shared.fact(f));
  return c;
}
